import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  Text,
  VStack,
  chakra,
  useToast,
} from "@chakra-ui/react";
import TextDisplay from "@/components/TextDisplay";
import { useConditionalBackground } from "@/contexts/useConditionalBackground ";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { phoneFormatter } from "@/utils/phoneFormatter";
import { cpfFormatter } from "@/utils/cpfFormatter";
import { FaUser } from "react-icons/fa";
import { useAuthCorbanStore } from "@/contexts/useAuthCorbanStore";
import { usePersonalDataStore } from "@/contexts/usePersonalDataStore";
import { updatePartialUser } from "@/services/antiFraud.service";
import { Field, FieldProps, Formik } from "formik";
import * as Yup from "yup";

interface AuthCorbanPersonalDataFormValues {
  whatsapp: string;
  email: string;
}

const ChakraForm = chakra("form");

const validationSchema = Yup.object().shape({
  whatsapp: Yup.string()
    .required("WhatsApp é obrigatório")
    .test(
      "valid-phone",
      "WhatsApp inválido. Digite um telefone válido com DDD",
      (value) => {
        if (!value) return false;
        const numbers = value.replace(/\D/g, "");
        return numbers.length >= 10 && numbers.length <= 11;
      }
    ),
  email: Yup.string().required("E-mail é obrigatório").email("E-mail inválido"),
});

export default function UserAuthenticationPersonalData() {
  const backgroundStyle = useConditionalBackground();
  const toast = useToast();
  const navigate = useNavigate();
  const { shorthash } = useParams<{ shorthash: string }>();
  const { userInfo } = useAuthCorbanStore();
  const { setPersonalData } = usePersonalDataStore();
  
  const initialValues: AuthCorbanPersonalDataFormValues = {
    whatsapp: userInfo?.user?.cellphone ? phoneFormatter(userInfo.user.cellphone) : "",
    email: userInfo?.user?.email ?? "",
  };

  useEffect(() => {
    if (!userInfo && shorthash) {
      navigate(`/plat`, { replace: true });
    }
  }, [navigate, shorthash, userInfo]);

  const handleConfirm = async (
    values: AuthCorbanPersonalDataFormValues
  ) => {
    if (!userInfo?.user?.userId) {
      toast({
        title: "Usuário não encontrado",
        description: "Valide o CPF novamente para continuar.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const cellphone = values?.whatsapp?.replace(/\D/g, "");

    try {
      if (cellphone !== userInfo?.user?.cellphone || values?.email?.trim() !== userInfo?.user?.email) {
        await updatePartialUser(userInfo?.user?.userId, {
          email: values?.email?.trim(),
          cellphone,
        });
      }

      setPersonalData(cellphone, values?.email?.trim());
      window.location.href =
        import.meta.env.VITE_ENV === "local"
          ? `https://cadastro.uat.unico.app/process/${userInfo?.unicoId}`
          : `https://cadastro.unico.app/process/${userInfo?.unicoId}`;
    } catch {
      toast({
        title: "Erro ao atualizar dados",
        description: "Não foi possível salvar o e-mail e o WhatsApp.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box
      minWidth="100vw"
      minHeight="100vh"
      {...backgroundStyle}
      display="flex"
      alignItems="flex-start"
      justifyContent="center"
      px={4}
    >
      <VStack mt="1.5rem" spacing={4} width="100%" maxWidth="400px" fontFamily="Lexend Deca">
        <Box
          width="100%"
          bg="whiteAlpha.350"
          borderRadius="md"
          borderWidth="1px"
          borderColor="whiteAlpha.500"
          p={2}
        >
          <Flex align="center" justify="center" gap={2}>
            <Icon as={FaUser} color="white" boxSize={4} />
            <Text
              fontSize="lg"
              fontWeight="medium"
              color="white"
              textAlign="center"
            >
              Dados do Vendedor
            </Text>
          </Flex>
        </Box>

        <VStack
          width="100%"
          spacing={5}
          bg="white"
          p={6}
          borderRadius="lg"
          boxShadow="lg"
          align="start"
          color="gray.800"
        >
          <Text
            fontSize="xl"
            fontWeight="bold"
            color="primary"
            textAlign="center"
            width="100%"
          >
            Dados Pessoais
          </Text>

          <Box width="100%">
            <Text fontSize="lg" fontWeight="medium" color="gray.600">
              Dados Basicos
            </Text>
            <Box bg="gray.50" padding="4" borderRadius="md" boxShadow="sm" mt="2">
              <VStack align="start" spacing="3">
                <TextDisplay
                  label="Nome Completo"
                  value={userInfo ? `${userInfo?.user?.name} ${userInfo?.user?.lastName}` : "—"}
                />
                <TextDisplay
                  label="CPF"
                  value={userInfo ? cpfFormatter(userInfo?.user?.document) : "—"}
                />
              </VStack>
            </Box>
          </Box>

          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={validationSchema}
            validateOnMount
            validateOnChange
            onSubmit={handleConfirm}
          >
            {({ errors, touched, handleSubmit, isValid, isSubmitting }) => (
              <ChakraForm width="100%" onSubmit={handleSubmit}>
                <FormControl isInvalid={!!errors.whatsapp && touched.whatsapp}>
                  <FormLabel htmlFor="whatsapp">WhatsApp</FormLabel>
                  <Field name="whatsapp">
                    {({ field, form }: FieldProps) => (
                      <Input
                        {...field}
                        id="whatsapp"
                        placeholder="Digite seu WhatsApp"
                        type="tel"
                        value={phoneFormatter(field.value || "")}
                        onChange={(e) => {
                          const formatted = phoneFormatter(e.target.value);
                          form.setFieldValue("whatsapp", formatted);
                        }}
                        fontSize="lg"
                        width="100%"
                        variant="flushed"
                        focusBorderColor="primary"
                        _placeholder={{ color: "primary.500" }}
                        paddingY="1rem"
                      />
                    )}
                  </Field>
                  <FormErrorMessage>{errors.whatsapp}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.email && touched.email} mt={4}>
                  <FormLabel htmlFor="email">E-mail</FormLabel>
                  <Field name="email">
                    {({ field }: FieldProps) => (
                      <Input
                        {...field}
                        id="email"
                        placeholder="Digite seu email"
                        type="email"
                        fontSize="lg"
                        width="100%"
                        variant="flushed"
                        focusBorderColor="primary"
                        _placeholder={{ color: "primary.500" }}
                        paddingY="1rem"
                      />
                    )}
                  </Field>
                  <FormErrorMessage>{errors.email}</FormErrorMessage>
                </FormControl>

                <Button
                  type="submit"
                  width="100%"
                  borderRadius="24px"
                  height="3rem"
                  marginTop="1.5rem"
                  fontSize="lg"
                  bg={isValid ? "primary" : "gray.300"}
                  color={isValid ? "white" : "gray.500"}
                  isDisabled={!isValid || isSubmitting}
                  _disabled={{
                    opacity: 1,
                    cursor: "not-allowed",
                  }}
                >
                  Confirmar e Prosseguir
                </Button>
              </ChakraForm>
            )}
          </Formik>
        </VStack>
      </VStack>
    </Box>
  );
}
